import React from "react";
import { Link } from "react-router-dom";
import { PlugZap, ArrowLeft, ExternalLink } from "lucide-react";

/**
 * Reusable "Connection Required" stub. Renders header + missing-credential
 * card so admins know exactly which env var to add. Used across menu items
 * gated on Stripe / Mapbox / HERE / Checkr / Onfido / S3 / Shopify Admin.
 */
export default function ConnectionRequiredPage({
  title,
  description,
  missingEnv = [],
  obtainAt,            // optional doc URL
  backTo = "/admin",
  children,
}) {
  return (
    <div
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10 space-y-6"
      data-testid="connection-required-page"
    >
      <div className="flex items-center gap-3">
        <Link
          to={backTo}
          className="text-xs text-lifted-dim hover:text-lifted-green inline-flex items-center gap-1"
        >
          <ArrowLeft size={12} /> Back
        </Link>
      </div>

      <header>
        <div className="eyebrow">Pending integration</div>
        <h1 className="font-anton text-3xl sm:text-4xl uppercase mt-2">
          {title}
        </h1>
        {description && (
          <p className="text-lifted-dim mt-3 max-w-2xl">{description}</p>
        )}
      </header>

      <div
        className="glass-panel p-6 border border-sky-400/30 flex items-start gap-3"
        data-testid="connection-required-card"
      >
        <PlugZap className="text-sky-200 mt-1 shrink-0" size={20} />
        <div className="flex-1">
          <div className="font-bebas tracking-[0.18em] uppercase text-sky-200 text-lg">
            Connection Required
          </div>
          <p className="text-xs text-lifted-dim mb-3">
            Add the following credentials to{" "}
            <code className="text-white">/app/backend/.env</code> and restart the
            backend. The rest of this page still works — only the live data feed
            is paused.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {missingEnv.map((e) => (
              <code
                key={e}
                className="text-xs bg-sky-500/10 text-sky-100 border border-sky-400/20 px-2 py-0.5 rounded"
                data-testid={`missing-env-${e}`}
              >
                {e}
              </code>
            ))}
          </div>
          {obtainAt && (
            <a
              href={obtainAt}
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-sky-200 hover:text-sky-100 inline-flex items-center gap-1 mt-3"
            >
              <ExternalLink size={11} /> How to obtain
            </a>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}
